import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  makeStyles,
  MenuItem,
  Select,
  Chip,
  OutlinedInput,
  IconButton,
  Button,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
// import DeleteIcon from "@material-ui/icons/Delete";
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from "@material-ui/icons/Edit";
import { commonInlineStyles, inputFieldStyles } from "../../styles";
import { CircularProgress } from "@material-ui/core";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";

const useStyles = makeStyles((theme) => ({
  formContainer: { padding: "24px" },
  formTitle: { marginBottom: theme.spacing(4), fontWeight: 500 },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  },
  button: { minWidth: 180, fontWeight: 600, fontSize: 16 },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "14px",
    marginTop: theme.spacing(0.5),
  },
  label: { marginBottom: theme.spacing(1), fontWeight: 500 },
  readOnlyValue: {
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
  chip: { margin: 2 },
  select: { minHeight: 120 },
  selectionsBox: {
    minHeight: 80,
    fontSize: 16,
    padding: 8,
    background: "#fafafa",
    border: "1px solid #ccc",
    borderRadius: 4,
    marginTop: 4,
    marginBottom: 16,
    whiteSpace: "pre-line",
  },
  selectContainer: {
    "& .MuiOutlinedInput-root": {
      padding: "0 !important",
      "& .MuiSelect-select": {
        padding: "12px 14px !important",
        boxSizing: "border-box !important",
      },
      "& fieldset": {
        border: "1px solid #ccc !important",
      },
      "&:hover fieldset": {
        border: "1px solid #FF6D00 !important", // Orange hover
      },
      "&.Mui-focused fieldset": {
        border: "2px solid #FF6D00 !important", // Slightly thicker when focused
        outline: "none !important",
      },
    },
    // Fix for the notch
    "& .MuiOutlinedInput-notchedOutline": {
      top: 0,
      "& legend": {
        display: "none !important",
      },
    },
    "& .MuiSelect-select": {
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  selectLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  }
}));

const PurchasingDetailsSection = forwardRef(
  (
    {
      initialData = {},
      onChange,
      disableEdit = false,
      editMode = false,
      onEditModeChange,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
    },
    ref
  ) => {
    const classes = useStyles();
    const [form, setForm] = useState({ ...initialData });
    const [errors, setErrors] = useState({});
    // API data states
    const [purchasingDetails, setPurchasingDetails] = useState(null);
    const [poDocTypes, setPoDocTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Selected values for multi-select
    const [selectedOrgs, setSelectedOrgs] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedPoDocTypes, setSelectedPoDocTypes] = useState([]);
    const [formChanged, setFormChanged] = useState(false);

    // New state for dropdown loading
    const [isDropdownLoading, setIsDropdownLoading] = useState(false);
    // const [cmoIds, setCmoIds] = useState([form.cmoIDFromSponsorERP || ""]);
      const [cmoIds, setCmoIds] = useState(
    form.cmoIDFromSponsorERP
      ? form.cmoIDFromSponsorERP.split(",").map((id) => id.trim())
      : [""]
  );
    const API_BASE_URL = useApiBaseUrl(appUrl)

    useEffect(() => {
      setForm({ ...initialData });
      setErrors({});
    }, [initialData.companyCode]);
    // Fetch purchasing details and PO document types
    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        setIsDropdownLoading(true);
        try {
          // Fetch purchasing org details
          const purchasingRes = await axios.get(
            `${API_BASE_URL}/fetchPurchasingDetails`
          );
          if (purchasingRes.data?.status) {
            setPurchasingDetails(purchasingRes.data.data);
          }

          // Fetch PO document types
          const poDocTypesRes = await axios.get(
            `${API_BASE_URL}/fetchPODocumentTypes`
          );
          if (poDocTypesRes.data?.status) {
            setPoDocTypes(poDocTypesRes.data.data.poDocumentTypeList);
          }
        } catch (error) {
          console.error("Error fetching purchasing data:", error);
        } finally {
          setLoading(false);
          setIsDropdownLoading(false);
        }
      };

      fetchData();
    }, []);

    // Initialize selected values from form data
    useEffect(() => {
      if (form) {
        // Initialize PO Document Types
        if (
          form.poDocumentTypesList &&
          Array.isArray(form.poDocumentTypesList)
        ) {
          setSelectedPoDocTypes(
            form.poDocumentTypesList.map((item) => item.poDocumentType)
          );
        } else if (form.poDocType && Array.isArray(form.poDocType)) {
          setSelectedPoDocTypes(form.poDocType);
        }

        // Initialize Purchasing Organizations
        if (form.purchasingOrgList && Array.isArray(form.purchasingOrgList)) {
          setSelectedOrgs(
            form.purchasingOrgList
              .map((org) => {
                // Handle different data structures
                if (org.purchasingOrgCode && org.purchasingOrgDescription) {
                  return `${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`;
                } else if (org.code && org.description) {
                  return `${org.code} / ${org.description}`;
                }
                return "";
              })
              .filter(Boolean)
          );
        }

        // Initialize Purchasing Groups
        if (
          form.purchasingGroupList &&
          Array.isArray(form.purchasingGroupList)
        ) {
          setSelectedGroups(
            form.purchasingGroupList
              .map((group) => {
                // Handle different data structures
                if (
                  group.purchasingGroupCode &&
                  group.purchasingGroupDescription &&
                  group.purchasingGroupTelephone
                ) {
                  return `${group.purchasingGroupCode} / ${group.purchasingGroupDescription} - ${group.purchasingGroupTelephone}`;
                } else if (
                  group.code &&
                  group.description &&
                  group.telephoneNbr
                ) {
                  return `${group.code} / ${group.description} - ${group.telephoneNbr}`;
                }
                return "";
              })
              .filter(Boolean)
          );
        }
      }
    }, [form]);

    const handleChange = (e) => {
      const { name, value } = e.target;

      // Apply specific validation for company code
      if (name === "companyCode") {
        const sanitizedValue = value.replace(/[^A-Za-z0-9]/g, "");
        if (sanitizedValue.length <= 10) {
          setForm((prev) => ({ ...prev, [name]: sanitizedValue }));
          setFormChanged(true);
          if (onChange) onChange({ ...form, [name]: sanitizedValue });
        }
      } else {
        setForm((prev) => ({ ...prev, [name]: value }));
        setFormChanged(true);
        if (onChange) onChange({ ...form, [name]: value });
      }

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    };

    const handleEdit = () => {
      if (onEditModeChange) {
        onEditModeChange(true);
      }
    };

    const handleCancel = () => {
      const cleanInitialData = JSON.parse(JSON.stringify(initialData));
      setForm(cleanInitialData);
      setErrors({});
      setFormChanged(false);

      if (onEditModeChange) {
        onEditModeChange(false);
      }

      // Reset selected values to match initial data
      if (cleanInitialData.poDocumentTypesList) {
        setSelectedPoDocTypes(
          cleanInitialData.poDocumentTypesList.map(
            (item) => item.poDocumentType
          )
        );
      } else if (cleanInitialData.poDocType) {
        setSelectedPoDocTypes(cleanInitialData.poDocType);
      } else {
        setSelectedPoDocTypes([]);
      }

      if (cleanInitialData.purchasingOrgList) {
        setSelectedOrgs(
          cleanInitialData.purchasingOrgList
            .map((org) => {
              if (org.purchasingOrgCode && org.purchasingOrgDescription) {
                return `${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`;
              } else if (org.code && org.description) {
                return `${org.code} / ${org.description}`;
              }
              return "";
            })
            .filter(Boolean)
        );
      } else {
        setSelectedOrgs([]);
      }

      if (cleanInitialData.purchasingGroupList) {
        setSelectedGroups(
          cleanInitialData.purchasingGroupList
            .map((group) => {
              if (
                group.purchasingGroupCode &&
                group.purchasingGroupDescription &&
                group.purchasingGroupTelephone
              ) {
                return `${group.purchasingGroupCode} / ${group.purchasingGroupDescription} - ${group.purchasingGroupTelephone}`;
              } else if (
                group.code &&
                group.description &&
                group.telephoneNbr
              ) {
                return `${group.code} / ${group.description} - ${group.telephoneNbr}`;
              }
              return "";
            })
            .filter(Boolean)
        );
      } else {
        setSelectedGroups([]);
      }
    };

    const handleOrgChange = (event) => {
      const selectedValues = event.target.value;
      setSelectedOrgs(selectedValues);

      // Get available groups for the new selection and filter existing selected groups
      const availableGroups = [];
      if (purchasingDetails?.purchasingOrgDetails) {
        purchasingDetails.purchasingOrgDetails.forEach((org) => {
          // Check if the selectedValues includes the org code and description, allowing for commas
          const orgString = `${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`;
          if (selectedValues.some(val => val.includes(orgString))) {
            org.purchasingGroupDetails.forEach((group) => {
              availableGroups.push(
                `${group.purchasingGroupCode} / ${group.purchasingGroupDescription} - ${group.purchasingGroupTelephone}`
              );
            });
          }
        });
      }

      // Filter existing selected groups to keep only those that are still available
      const filteredSelectedGroups = selectedGroups.filter((group) =>
        availableGroups.includes(group)
      );
      setSelectedGroups(filteredSelectedGroups);

      const updatedForm = {
        ...form,
        purchasingOrgList: selectedValues.map((org) => {
          // Split by first occurrence of ' / ' to handle commas in description
          const parts = org.split(' / ');
          const code = parts[0];
          const description = parts.slice(1).join(' / '); // Join back any remaining parts
          return {
            purchasingOrgCode: code,
            purchasingOrgDescription: description,
          };
        }),
        purchasingGroupList: filteredSelectedGroups.map((group) => {
          // Split by first occurrence of ' / ' to handle commas in description
          const parts = group.split(' / ');
          const code = parts[0];
          const restParts = parts.slice(1).join(' / ');
          // Split by first occurrence of ' - ' to handle commas in telephone
          const [description, telephoneNbr] = restParts.split(' - ');
          return {
            purchasingGroupCode: code,
            purchasingGroupDescription: description,
            purchasingGroupTelephone: telephoneNbr,
          };
        }),
      };

      setForm(updatedForm);
      setFormChanged(true);
      if (onChange) onChange(updatedForm);

      if (errors.purchasingOrg) {
        setErrors((prev) => ({ ...prev, purchasingOrg: "" }));
      }
    };

    const handleGroupChange = (event) => {
      const selectedValues = event.target.value;
      setSelectedGroups(selectedValues);

      const updatedForm = {
        ...form,
        purchasingGroupList: selectedValues.map((group) => {
          // Split by first occurrence of ' / ' to handle commas in description
          const parts = group.split(' / ');
          const code = parts[0];
          const restParts = parts.slice(1).join(' / ');
          // Split by first occurrence of ' - ' to handle commas in telephone
          const [description, telephoneNbr] = restParts.split(' - ');
          return {
            purchasingGroupCode: code,
            purchasingGroupDescription: description,
            purchasingGroupTelephone: telephoneNbr,
          };
        }),
      };

      setForm(updatedForm);
      setFormChanged(true);
      if (onChange) onChange(updatedForm);

      if (errors.purchasingGroup) {
        setErrors((prev) => ({ ...prev, purchasingGroup: "" }));
      }
    };

    const handlePoDocTypeChange = (event) => {
      const selectedValues = event.target.value;
      setSelectedPoDocTypes(selectedValues);

      // Update form data with proper structure for API
      const updatedForm = {
        ...form,
        poDocType: selectedValues,
        poDocumentTypesList: selectedValues.map((type) => ({
          poDocumentType: type,
        })),
      };

      setForm(updatedForm);
      setFormChanged(true);

      // Clear error if it exists
      if (errors.poDocType) {
        setErrors((prev) => ({ ...prev, poDocType: "" }));
      }

      if (onChange) {
        onChange(updatedForm, true);
      }
    };

    // Handle CMO ID change with validation
    // const handleCmoIdChange = (e) => {
    //   const value = e.target.value.replace(/[^A-Za-z0-9\-_]/g, "");
    //   if (value.length <= 15) {
    //     setForm((prev) => ({ ...prev, cmoIDFromSponsorERP: value }));
    //     setFormChanged(true);
    //     if (onChange) onChange({ ...form, cmoIDFromSponsorERP: value });

    //     if (errors.cmoIDFromSponsorERP) {
    //       setErrors((prev) => ({ ...prev, cmoIDFromSponsorERP: "" }));
    //     }
    //   }
    // };

const checkForDuplicateCmoIds = (cmoIdsArray) => {
  const trimmedIds = cmoIdsArray.map(id => id.trim().toLowerCase()).filter(Boolean);
  const duplicates = trimmedIds.filter((id, index) => trimmedIds.indexOf(id) !== index);
  return duplicates.length > 0 ? [...new Set(duplicates)] : [];
};

// Modified handleDynamicCmoIdChange function
const handleDynamicCmoIdChange = (index, value) => {
  const cleanedValue = value.replace(/[^A-Za-z0-9\-_]/g, "");
  if (cleanedValue.length <= 15) {
    const updated = [...cmoIds];
    updated[index] = cleanedValue;
    
    // Check for duplicates
    const duplicates = checkForDuplicateCmoIds(updated);
    if (duplicates.length > 0) {
      setErrors((prev) => ({ 
        ...prev, 
        cmoIDFromSponsorERP: `CMO ID already exists: ${duplicates.join(", ")}. Please enter a different CMO ID.` 
      }));
    } else {
      // Clear duplicate error if no duplicates found
      setErrors((prev) => ({ 
        ...prev, 
        cmoIDFromSponsorERP: "" 
      }));
    }
    
    setCmoIds(updated);

    // Update form with comma-separated string
    const newString = updated.filter(Boolean).join(",");
    setForm((prev) => ({ ...prev, cmoIDFromSponsorERP: newString }));
    setFormChanged(true);
    if (onChange) onChange({ ...form, cmoIDFromSponsorERP: newString });
  }
};

//     const handleDynamicCmoIdChange = (index, value) => {
//   const cleanedValue = value.replace(/[^A-Za-z0-9\-_]/g, "");
//   if (cleanedValue.length <= 15) {
//     const updated = [...cmoIds];
//     updated[index] = cleanedValue;
//     setCmoIds(updated);

//     // also keep your existing form logic updated as comma-separated string
//     const newString = updated.filter(Boolean).join(",");
//     setForm((prev) => ({ ...prev, cmoIDFromSponsorERP: newString }));
//     setFormChanged(true);
//     if (onChange) onChange({ ...form, cmoIDFromSponsorERP: newString });

//     if (errors.cmoIDFromSponsorERP) {
//       setErrors((prev) => ({ ...prev, cmoIDFromSponsorERP: "" }));
//     }
//   }
// };

const handleAddCmoId = (index) => {
  // prevent adding if any field is empty
  if (cmoIds.some((id) => id.trim() === "")) {
    return;
  }
  const updated = [...cmoIds,""];
  // updated.splice(index + 1, 0, ""); // insert new field after clicked index
  setCmoIds(updated);

  //   if (errors.cmoIDFromSponsorERP) {
  //   setErrors((prev) => ({ ...prev, cmoIDFromSponsorERP: "" }));
  // }
};



// const handleRemoveCmoId = (index) => {
//   const updated = cmoIds.filter((_, i) => i !== index);
//   setCmoIds(updated);

//   // sync to form
//   const newString = updated.filter(Boolean).join(",");
//   setForm((prev) => ({ ...prev, cmoIDFromSponsorERP: newString }));
// };


const handleRemoveCmoId = (index) => {
  const updated = cmoIds.filter((_, i) => i !== index);
  setCmoIds(updated);

  // sync to form
  const newString = updated.filter(Boolean).join(",");
  const updatedForm = { ...form, cmoIDFromSponsorERP: newString };
  setForm(updatedForm);
  
  // Add these lines to trigger change detection
  setFormChanged(true);
  if (onChange) onChange(updatedForm);

  // Clear any existing errors
  // if (errors.cmoIDFromSponsorERP) {
  //   setErrors((prev) => ({ ...prev, cmoIDFromSponsorERP: "" }));
  // }
  const remainingIds = updated.filter(Boolean);
 if (remainingIds.length > 0) {
   const duplicates = checkForDuplicateCmoIds(remainingIds);
   if (duplicates.length > 0) {
     setErrors((prev) => ({
       ...prev,
       cmoIDFromSponsorERP: `Duplicate CMO IDs found: ${duplicates.join(
         ", "
       )}. Each CMO ID must be unique.`,
     }));
   } else {
     // Only clear error if no duplicates found
     setErrors((prev) => ({ ...prev, cmoIDFromSponsorERP: "" }));
   }
 } else {
   // Clear error if no CMO IDs left
   setErrors((prev) => ({ ...prev, cmoIDFromSponsorERP: "" }));
 }
};

    // Get available groups for selected orgs
    const getAvailableGroups = () => {
      if (!purchasingDetails?.purchasingOrgDetails) return [];

      const groups = [];
      purchasingDetails.purchasingOrgDetails.forEach((org) => {
        if (
          selectedOrgs.includes(
            `${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`
          )
        ) {
          org.purchasingGroupDetails.forEach((group) => {
            groups.push(
              `${group.purchasingGroupCode} / ${group.purchasingGroupDescription} - ${group.purchasingGroupTelephone}`
            );
          });
        }
      });
      return groups;
    };

    const validate = () => {
      let valid = true;
      let errs = {};

      // if (!form.companyCode) {
      //   errs.companyCode = "Company Code required";
      //   valid = false;
      // } else if (!/^[A-Za-z0-9]{1,10}$/.test(form.companyCode)) {
      //   errs.companyCode =
      //     "Company Code must be alphanumeric only, maximum 10 characters";
      //   valid = false;
      // }

      // CMO ID validation - MANDATORY
      const cmoIdsArray = (form?.cmoIDFromSponsorERP || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      if (cmoIdsArray.length === 0) {
        errs.cmoIDFromSponsorERP = "At least one CMO ID is required";
        valid = false;
      } else {
        // Check for duplicates first
        const duplicates = checkForDuplicateCmoIds(cmoIdsArray);
        if (duplicates.length > 0) {
          errs.cmoIDFromSponsorERP = `Duplicate CMO IDs found: ${duplicates.join(", ")}. Each CMO ID must be unique.`;
          valid = false;
        } else {
          // Check format validation only if no duplicates
          cmoIdsArray.forEach((id) => {
            if (!/^[A-Za-z0-9\-_]{1,15}$/.test(id)) {
              errs.cmoIDFromSponsorERP =
                "Each CMO ID must be alphanumeric and may include hyphens (-) and underscores (_) only, max 15 characters";
              valid = false;
            }
          });
        }
      }

      // if (!form.purchasingOrgList || form.purchasingOrgList.length === 0) {
      //   errs.purchasingOrg =
      //     "At least one Purchasing Organization must be selected";
      //   valid = false;
      // }
      // if (!form.purchasingGroupList || form.purchasingGroupList.length === 0) {
      //   errs.purchasingGroup = "At least one Purchasing Group must be selected";
      //   valid = false;
      // }
      // if (!form.poDocumentTypesList || form.poDocumentTypesList.length === 0) {
      //   errs.poDocType = "At least one PO Document Type must be selected";
      //   valid = false;
      // }

      // if (!form.purchasingOrgList || form.purchasingOrgList.length === 0) {
      //   errs.purchasingOrg =
      //     "At least one Purchasing Organization must be selected";
      //   valid = false;
      // }
      // if (!form.purchasingGroupList || form.purchasingGroupList.length === 0) {
      //   errs.purchasingGroup = "At least one Purchasing Group must be selected";
      //   valid = false;
      // }
      // if (!form.poDocumentTypesList || form.poDocumentTypesList.length === 0) {
      //   errs.poDocType = "At least one PO Document Type must be selected";
      //   valid = false;
      // }

      setErrors(errs);
      return valid;
    };

    // Watch for form changes
    useEffect(() => {
      const hasChanges = JSON.stringify(form) !== JSON.stringify(initialData);
      setFormChanged(hasChanges);
    }, [form, initialData]);

    const handleSave = () => {
      if (validate()) {
        // Exit edit mode
        if (onEditModeChange) {
          onEditModeChange(false);
        }
      }
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => form,
      getPayload: () => {
        // Create the payload structure that matches the expected format
        const formData = {
          companyCode: form.companyCode,
          cmoIDFromSponsorERP: form.cmoIDFromSponsorERP,
          purchasingOrgList:
            form.purchasingOrgList && form.purchasingOrgList.length > 0
              ? form.purchasingOrgList
              : selectedOrgs.map((org) => {
                // Split by first occurrence of ' / ' to handle commas in description
                const parts = org.split(' / ');
                const code = parts[0];
                const description = parts.slice(1).join(' / '); // Join back any remaining parts
                return {
                  purchasingOrgCode: code,
                  purchasingOrgDescription: description,
                };
              }),
          purchasingGroupList:
            form.purchasingGroupList && form.purchasingGroupList.length > 0
              ? form.purchasingGroupList
              : selectedGroups.map((group) => {
                // Split by first occurrence of ' / ' to handle commas in description
                const parts = group.split(' / ');
                const code = parts[0];
                const restParts = parts.slice(1).join(' / ');
                // Split by first occurrence of ' - ' to handle commas in telephone
                const [description, telephoneNbr] = restParts.split(' - ');
                return {
                  purchasingGroupCode: code,
                  purchasingGroupDescription: description,
                  purchasingGroupTelephone: telephoneNbr,
                };
              }),
          poDocumentTypesList:
            form.poDocumentTypesList && form.poDocumentTypesList.length > 0
              ? form.poDocumentTypesList.map(
                (item) => item.poDocumentType || item
              )
              : selectedPoDocTypes,
        };

        return {
          vendorMosymphonyID: vendorMosymphonyID,
          brandOwnerMosymphonyID: brandOwnerMosymphonyID,
          updateCMOPurchasingDetailsFlag: true,
          vendorPlantDetails: {
            ...formData,
            receivingPlantIDs: [],
            receivingStorageLocationIDs: [],
          },
        };
      },
      validateForm: validate,
      resetForm: () => {
        const cleanInitialData = JSON.parse(JSON.stringify(initialData));
        setForm(cleanInitialData);
        setErrors({});
        setFormChanged(false);

        // Reset selected values for multi-selects
        if (initialData.poDocumentTypesList) {
          setSelectedPoDocTypes(
            initialData.poDocumentTypesList.map((item) => item.poDocumentType)
          );
        } else if (initialData.poDocType) {
          setSelectedPoDocTypes(initialData.poDocType);
        } else {
          setSelectedPoDocTypes([]);
        }

        if (initialData.purchasingOrgList) {
          setSelectedOrgs(
            initialData.purchasingOrgList
              .map((org) => {
                if (org.purchasingOrgCode && org.purchasingOrgDescription) {
                  return `${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`;
                } else if (org.code && org.description) {
                  return `${org.code} / ${org.description}`;
                }
                return "";
              })
              .filter(Boolean)
          );
        } else {
          setSelectedOrgs([]);
        }

        if (initialData.purchasingGroupList) {
          setSelectedGroups(
            initialData.purchasingGroupList
              .map((group) => {
                if (
                  group.purchasingGroupCode &&
                  group.purchasingGroupDescription &&
                  group.purchasingGroupTelephone
                ) {
                  return `${group.purchasingGroupCode} / ${group.purchasingGroupDescription} - ${group.purchasingGroupTelephone}`;
                } else if (
                  group.code &&
                  group.description &&
                  group.telephoneNbr
                ) {
                  return `${group.code} / ${group.description} - ${group.telephoneNbr}`;
                }
                return "";
              })
              .filter(Boolean)
          );
        } else {
          setSelectedGroups([]);
        }
      },
    }));

    // Memoize the menu items to prevent unnecessary re-renders
    const poDocTypeMenuItems = React.useMemo(() => {
      return poDocTypes.map((type) => (
        <MenuItem
          key={type.poDocumentType || type.poDocumentTypeCode}
          value={type.poDocumentType || type.poDocumentTypeCode}
        >
          <Checkbox
            checked={
              selectedPoDocTypes.indexOf(
                type.poDocumentType || type.poDocumentTypeCode
              ) > -1
            }
          />
          <ListItemText
            primary={
              type.poDocumentType || type.poDocumentTypeCode
            }
          />
        </MenuItem>
      ));
    }, [poDocTypes, selectedPoDocTypes]);

    // Read-only view
    // if (!editMode) {
    //   return (
    //     <div className={classes.formContainer}>
    //       <Grid container alignItems="center" justifyContent="space-between">
    //         <Grid item>
    //           <Typography variant="h6" className={classes.formTitle}>
    //             Purchasing Details
    //           </Typography>
    //         </Grid>
    //       </Grid>
    //       <Grid container spacing={3}>
    //         <Grid item xs={12} sm={6}>
    //           <Typography component="label" className={classes.label}>
    //             Company Code
    //           </Typography>
    //           <div className={classes.readOnlyValue}>
    //             {form.companyCode || "-"}
    //           </div>
    //         </Grid>
    //         <Grid item xs={12} sm={6}>
    //           <Typography component="label" className={classes.label}>
    //             PO Document Type
    //           </Typography>
    //           <div className={classes.readOnlyValue}>
    //             {form.poDocumentTypesList
    //               ?.map((item) => item.poDocumentType)
    //               .join(", ") ||
    //               (Array.isArray(form.poDocType)
    //                 ? form.poDocType.join(", ")
    //                 : form.poDocType) ||
    //               "-"}
    //           </div>
    //         </Grid>
    //         <Grid item xs={12} sm={6}>
    //           <Typography component="label" className={classes.label}>
    //             Purchasing Org Code / Description
    //           </Typography>
    //           <div className={classes.readOnlyValue}>
    //             {form.purchasingOrgList
    //               ?.map((org) => {
    //                 if (org.purchasingOrgCode && org.purchasingOrgDescription) {
    //                   return `${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`;
    //                 } else if (org.code && org.description) {
    //                   return `${org.code} / ${org.description}`;
    //                 }
    //                 return "";
    //               })
    //               .filter(Boolean)
    //               .join(", ") || "-"}
    //           </div>
    //         </Grid>
    //         <Grid item xs={12} sm={6}>
    //           <Typography component="label" className={classes.label}>
    //             Purchasing Group Code / Description - Telephone
    //           </Typography>
    //           <div className={classes.readOnlyValue}>
    //             {form.purchasingGroupList
    //               ?.map((group) => {
    //                 if (
    //                   group.purchasingGroupCode &&
    //                   group.purchasingGroupDescription &&
    //                   group.purchasingGroupTelephone
    //                 ) {
    //                   return `${group.purchasingGroupCode} / ${group.purchasingGroupDescription} - ${group.purchasingGroupTelephone}`;
    //                 } else if (
    //                   group.code &&
    //                   group.description &&
    //                   group.telephoneNbr
    //                 ) {
    //                   return `${group.code} / ${group.description} - ${group.telephoneNbr}`;
    //                 }
    //                 return "";
    //               })
    //               .filter(Boolean)
    //               .join(", ") || "-"}
    //           </div>
    //         </Grid>
    //         <Grid item xs={12} sm={6}>
    //           <Typography component="label" className={classes.label}>
    //             CMO ID from Sponsor ERP
    //           </Typography>
    //           <div className={classes.readOnlyValue}>
    //             {form.cmoIDFromSponsorERP || "-"}
    //           </div>
    //         </Grid>
    //       </Grid>
    //     </div>
    //   );
    // }

    // Edit mode
    return (
      <div className="cmo-stepper-wrapper">
        <div className="stepper-container">
          <form autoComplete="off">
            <Grid className="two-col-forms-grid" container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="companyCode">
                    Company Code 
                    {/* {editMode && <span style={{ color: "red" }}>*</span>} */}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.companyCode || ""}</Typography>
                  ) : (
                    <TextField
                      id="companyCode"
                      name="companyCode"
                      value={form.companyCode || ""}
                      onChange={handleChange} 
                      onBlur={(e) => {
                        const { name, value } = e.target;
                        // Trim leading and trailing spaces for string values
                        if (typeof value === "string" && value.trim() !== value) {
                          handleChange({ target: { name, value: value.trim() } });
                        }
                      }}
                      variant="outlined"
                      placeholder="Enter text here"
                      fullWidth
                      required
                      error={!!errors.companyCode}
                      helperText={errors.companyCode || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={commonInlineStyles.inputProps}
                      style={commonInlineStyles.input}
                    />
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="poDocType">
                    PO Document Type 
                    {/* {editMode && <span style={{ color: "red" }}>*</span>} */}
                  </Typography>
                  {editMode && (
                    <Select
                      multiple
                      id="poDocType"
                      name="poDocType"
                      value={selectedPoDocTypes}
                      className={classes.selectContainer}
                      onChange={handlePoDocTypeChange}
                      input={
                        <OutlinedInput
                          label="PO Document Type"
                          notched={false}
                        />
                      }
                      renderValue={(selected) =>
                        selected.length === 0 ? "Select from the list" : selected.join(", ")
                      }
                      fullWidth
                      required
                      error={!!errors.poDocType}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          }
                        },
                        variant: "menu",
                        transitionDuration: 0 // Disable animation for faster rendering
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      {isDropdownLoading ? (
                        <div className={classes.selectLoading}>
                          <CircularProgress size={24} />
                          <span style={{ marginLeft: 10 }}>Loading options...</span>
                        </div>
                      ) : (
                        poDocTypeMenuItems
                      )}
                    </Select>
                  )}
                  <div className={classes.selectionsBox}>
                    {selectedPoDocTypes.length > 0
                      ? selectedPoDocTypes.join("\n")
                      : "Your Selections"}
                  </div>
                  {errors.poDocType && (
                    <Typography className={classes.errorText} style={{ fontSize: 14 }}>
                      {errors.poDocType}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="purchasingOrg">
                    Purchasing Org Code / Description
                    {/* {editMode && <span style={{ color: "red" }}>*</span>} */}
                  </Typography>
                  {editMode && (
                    <Select
                      id="purchasingOrg"
                      name="purchasingOrg"
                      multiple
                      value={selectedOrgs}
                      onChange={handleOrgChange}
                      className={classes.selectContainer}
                      input={
                        <OutlinedInput
                          label="Purchasing Org Code / Description"
                          notched={false}
                        />
                      }
                      renderValue={(selected) =>
                        selected.length === 0 ? "Select from the list" : selected.join(", \n")
                      }
                      fullWidth
                      required
                      error={!!errors.purchasingOrg}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      {purchasingDetails?.purchasingOrgDetails?.map((org) => (
                        <MenuItem
                          key={`${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`}
                          value={`${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`}
                        >
                          <Checkbox
                            checked={
                              selectedOrgs.some(val => val.includes(`${org.purchasingOrgCode} / `))
                            }
                          />
                          <ListItemText
                            primary={`${org.purchasingOrgCode} / ${org.purchasingOrgDescription}`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  <div className={classes.selectionsBox}>
                    {selectedOrgs.length > 0
                      ? selectedOrgs.join(", \n")
                      : "Your Selections"}
                  </div>
                  {errors.purchasingOrg && (
                    <Typography className={classes.errorText} style={{ fontSize: 14 }}>
                      {errors.purchasingOrg}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="purchasingGroup">
                    Purchasing Group Code / Description - Telephone
                    {/* {editMode && <span style={{ color: "red" }}>*</span>} */}
                  </Typography>
                  {editMode && (
                    <Select
                      id="purchasingGroup"
                      name="purchasingGroup"
                      multiple
                      value={selectedGroups}
                      onChange={handleGroupChange}
                      className={classes.selectContainer}
                      input={
                        <OutlinedInput
                          label="Purchasing Group Code / Description - Telephone"
                          notched={false}
                        />
                      }
                      renderValue={(selected) =>
                        selected.length === 0 ? "Select from the list" : selected.join(", \n")
                      }
                      fullWidth
                      required
                      disabled={selectedOrgs.length === 0}
                      error={!!errors.purchasingGroup}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      {getAvailableGroups().map((group) => (
                        <MenuItem key={group} value={group}>
                          <Checkbox
                            checked={selectedGroups.some(val => val.includes(group.split(' / ')[0] + ' / '))}
                          />
                          <ListItemText primary={group} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  <div className={classes.selectionsBox}>
                    {selectedGroups.length > 0
                      ? selectedGroups.join(", \n")
                      : "Your Selections"}
                  </div>
                  {errors.purchasingGroup && (
                    <Typography className={classes.errorText} style={{ fontSize: 14 }}>
                      {errors.purchasingGroup}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="cmoIDFromSponsorERP">
                    CMO ID from Sponsor ERP{" "}
                    {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.cmoIDFromSponsorERP || ""}</Typography>
                  ) : (
                    <TextField
                      id="cmoIDFromSponsorERP"
                      name="cmoIDFromSponsorERP"
                      value={form.cmoIDFromSponsorERP || ""}
                      onChange={handleCmoIdChange}
                      variant="outlined"
                      placeholder="Enter text here"
                      fullWidth
                      required
                      error={!!errors.cmoIDFromSponsorERP}
                      helperText={errors.cmoIDFromSponsorERP || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={commonInlineStyles.inputProps}
                      style={commonInlineStyles.input}
                    />
                  )}
                </FormControl>
              </Grid> */}

              <Grid item xs={12} sm={6}>
  <FormControl className={classes.formField}>
    <Typography component="label" htmlFor="cmoIDFromSponsorERP">
      CMO ID from Sponsor ERP{" "}
      {editMode && <span style={{ color: "red" }}>*</span>}
    </Typography>

    {!editMode ? (
      <Typography>{form.cmoIDFromSponsorERP || ""}</Typography>
    ) : (
      <>
        {cmoIds.map((id, index) => (
          <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
            <TextField
              id={`cmoIDFromSponsorERP-${index}`}
              name={`cmoIDFromSponsorERP-${index}`}
              value={id}
              onChange={(e) => handleDynamicCmoIdChange(index, e.target.value)}
              onBlur={(e) => {
                const { name, value } = e.target;
                // Trim leading and trailing spaces for string values
                if (typeof value === "string" && value.trim() !== value) {
                  handleDynamicCmoIdChange(index,value.trim());
                }
              }}
              variant="outlined"
              placeholder="Enter text here"
              fullWidth
              required
              // error={!!errors.cmoIDFromSponsorERP}
              // helperText={errors.cmoIDFromSponsorERP || ""}
              InputProps={{ style: commonInlineStyles.input }}
              sx={inputFieldStyles}
              inputProps={commonInlineStyles.inputProps}
              style={commonInlineStyles.input}
            />
            {index > 0 && (
              <IconButton onClick={() => handleRemoveCmoId(index)} style={{color:"red"}} aria-label="delete">
                {/* <DeleteIcon /> */}
                <DeleteOutlinedIcon />
              </IconButton>
            )}
               {index === 0 && (
            <IconButton onClick={() => handleAddCmoId(index)} style={{color:"black"}} aria-label="add">
              <AddCircleOutlineIcon />
            </IconButton>
               )}
                 </div>
        ))}
             {errors.cmoIDFromSponsorERP && (
           <Typography 
        className={classes.errorText} 
        style={{ 
          fontSize: 14, 
          color: "#f44336", 
        }}
      >
   {errors.cmoIDFromSponsorERP}
      </Typography>
      )}
      </>
    )}
  </FormControl>
</Grid>

              {/* Plant IDs From Sponsor ERP */}
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="plantIDsFromSponsorERP">
                    Plant IDs From Sponsor ERP
                  </Typography>
                  <Typography>
                    {form?.receivingPlantIDs && form.receivingPlantIDs.length > 0 
                      ? form.receivingPlantIDs.join(", ") 
                      : "NA"}
                  </Typography>
                </FormControl>
              </Grid>

              {/* Storage Location IDs From Sponsor ERP */}
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="storageLocationIDsFromSponsorERP">
                    Storage Location IDs From Sponsor ERP
                  </Typography>
                  <Typography>
                    {form?.receivingStorageLocationIDs && form.receivingStorageLocationIDs.length > 0 
                      ? form.receivingStorageLocationIDs.join(", ") 
                      : "NA"}
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
    );
  }
);

export default PurchasingDetailsSection;
